import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import Tooltip from '../../components/Tooltip';
import SupportTicketForm from '../../components/SupportTicketForm';
import Toast from '../../components/Toast';
import { formatTimeAgo } from '../../utils/time';
import { validatePhone } from '../../utils/validation';
// Use the same interface structure as CandidateOnboarding for consistency
interface ContactInfo {
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  email?: string;
  country_code?: string;
  phone?: string;
  alternate_email?: string;
  alternate_phone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  linkedin_url?: string;
  github_url?: string;
  portfolio_url?: string;
  twitter_url?: string;
  personal_website?: string;
  confidence?: number;
}

interface ProfessionalSummary {
  summary?: string;
  objective?: string;
  current_title?: string;
  years_experience?: number;
  career_level?: string;
  availability?: string;
  work_type_preference?: string;
  willing_to_relocate?: boolean;
  work_authorization?: string;
  expected_salary?: number;
  salary_currency?: string;
  confidence?: number;
}

interface WorkExperience {
  company_name?: string;
  job_title?: string;
  department?: string;
  employment_type?: string;
  location?: string;
  is_remote?: boolean;
  start_date?: string;
  end_date?: string;
  is_current?: boolean;
  duration_text?: string;
  description?: string;
  responsibilities?: string[];
  achievements?: string[];
  projects?: string[];
  technologies?: string[];
  skills_used?: string[];
  team_size?: number;
  reports?: number;
  metrics?: string[];
  confidence?: number;
}

interface Education {
  institution_name?: string;
  institution_type?: string;
  location?: string;
  degree_type?: string;
  degree_name?: string;
  field_of_study?: string;
  major?: string;
  minor?: string;
  concentration?: string;
  gpa?: number;
  gpa_scale?: string;
  honors?: string[];
  start_date?: string;
  end_date?: string;
  graduation_year?: number;
  is_current?: boolean;
  thesis_title?: string;
  relevant_coursework?: string[];
  activities?: string[];
  projects?: string[];
  confidence?: number;
}

interface Skill {
  name?: string;
  category?: string;
  proficiency?: string;
  years_experience?: number;
  last_used?: string;
  context?: string[];
  mentioned_count?: number;
  confidence?: number;
}

interface AIProfileData {
  contact_info?: ContactInfo;
  professional_summary?: ProfessionalSummary;
  work_experience?: WorkExperience[];
  education?: Education[];
  skills?: Skill[];
  certifications?: any[];
  languages?: any[];
  projects?: any[];
  awards?: any[];
  analysis?: any;
  processing_metadata?: Record<string, any>;
  overall_confidence?: number;
}

interface Notification {
  id: string;
  type: string;
  priority: string;
  title: string;
  message: string;
  action_url?: string;
  action_text?: string;
  is_read: boolean;
  created_at: string;
  read_at?: string;
  company_name?: string;
  job_title?: string;
}

interface NotificationResponse {
  notifications: Notification[];
  total: number;
  unread_count: number;
  has_more: boolean;
}

interface AnalyticsData {
  profile_views: number;
  search_appearances: number;
  recruiter_contacts: number;
  keywords_matched: string[];
  top_skills: string[];
  profile_strength: number;
  verification_status: {
    id_verified: boolean;
    background_check: boolean;
    skill_assessment: boolean;
  };
  recent_activity: Array<{
    type: string;
    company: string;
    date: string;
    time_ago: string;
  }>;
  profile_view_trends: Array<{
    date: string;
    views: number;
  }>;
  top_search_keywords: Array<{
    keyword: string;
    count: number;
  }>;
  top_searched_job_titles: Array<{
    job_title: string;
    search_count: number;
  }>;
}

const sanitizePhoneInput = (value: string): string => {
  return value.replace(/\D/g, '');
};

const sanitizeCountryCodeInput = (value: string): string => {
  const codeCharsOnly = value.replace(/[^\d+]/g, '');
  return codeCharsOnly.replace(/(?!^)\+/g, '');
};

export default function CandidateDashboard() {
  const { user, loading: authLoading, updateUserState } = useAuth();
  const navigate = useNavigate();
  const [hasProfile, setHasProfile] = useState(false);
  const [aiProfileData, setAiProfileData] = useState<AIProfileData | null>(null);
  const [candidateProfile, setCandidateProfile] = useState<any | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showProfileReview, setShowProfileReview] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState<string>('');
  const [uploadError, setUploadError] = useState<string>('');
  const [phoneValidationError, setPhoneValidationError] = useState<string>('');
  const [profileExists, setProfileExists] = useState(false);
  const [notifications, setNotifications] = useState<NotificationResponse>({
    notifications: [],
    total: 0,
    unread_count: 0,
    has_more: false
  });
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [processingStatus, setProcessingStatus] = useState<string>('');
  const [publicProfileUrl, setPublicProfileUrl] = useState<string | null>(null);
  const [isGeneratingResume, setIsGeneratingResume] = useState(false);
  const [lastResumeUrl, setLastResumeUrl] = useState<string | null>(null);
  const [error, setError] = useState<string>('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [showSupportForm, setShowSupportForm] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);

  useEffect(() => {
    if (authLoading) return;

    // Restore saved profile review data
    const savedProfileData = sessionStorage.getItem('dashboard_profile_data');
    if (savedProfileData && !aiProfileData) {
      try {
        const parsedData = JSON.parse(savedProfileData);
        setAiProfileData(parsedData);
        setShowProfileReview(true);
      } catch {
        sessionStorage.removeItem('dashboard_profile_data');
      }
    }

    // Restore in-progress resume processing
    const processingState = sessionStorage.getItem('dashboard_processing_state');
    if (processingState) {
      try {
        const state = JSON.parse(processingState);
        if (Date.now() - state.timestamp < 10 * 60 * 1000) {
          setIsProcessing(true);
          setProcessingStatus(state.status || 'Processing resume...');
          setTimeout(() => pollTaskStatus(state.taskId), 1000);
        } else {
          sessionStorage.removeItem('dashboard_processing_state');
        }
      } catch {
        sessionStorage.removeItem('dashboard_processing_state');
      }
    }

    checkProfileAndLoadNotifications();
  }, [authLoading, user]);

  const checkProfileAndLoadNotifications = async () => {
    try {
      setLoading(true);
      setError('');

      if (authLoading) {
        console.log('⏳ CandidateDashboard: Waiting for auth to initialize for dashboard data...');
        return;
      }

      if (!user) {
        console.log('❌ CandidateDashboard: User not authenticated, skipping dashboard data load');
        return;
      }

      console.log('📊 CandidateDashboard: Loading dashboard data...');

      // Check flags before making API call
      const hasSkippedOnboarding = sessionStorage.getItem('skip_onboarding') === 'true';
      const profileCreationCompleted = sessionStorage.getItem('profile_creation_completed') === 'true';

      console.log('📦 CandidateDashboard: Dashboard load - SessionStorage flags:', {
        hasSkippedOnboarding,
        profileCreationCompleted
      });

      let profileCheckResult = null;

      if (!hasSkippedOnboarding) {
        console.log('🔍 CandidateDashboard: Checking backend for profile...');
        profileCheckResult = await apiService.checkCandidateProfileExists();
        console.log('📊 CandidateDashboard: Dashboard load - Profile check result:', profileCheckResult);

        if (!profileCheckResult.has_profile) {
          console.log('🚀 CandidateDashboard: No profile during dashboard load, redirecting to onboarding');
          navigate('/candidate/onboarding', { replace: true });
          return;
        } else if (profileCheckResult.can_claim) {
          console.log('🎯 CandidateDashboard: User has company-owned profile that can be claimed, redirecting to onboarding');
          navigate('/candidate/onboarding', { replace: true });
          return;
        } else {
          console.log('✅ CandidateDashboard: Profile found in backend, setting completion flag');
          sessionStorage.setItem('profile_creation_completed', 'true');
        }
      } else {
        console.log('✅ CandidateDashboard: User has skipped onboarding, skipping profile check');
      }

      setHasProfile(true);

      // Set profileExists based on the actual profile check result, not whether API calls succeed
      const hasSkippedOnboarding2 = sessionStorage.getItem('skip_onboarding') === 'true';

      if (hasSkippedOnboarding2) {
        console.log('⏭️ CandidateDashboard: User skipped onboarding, profile is incomplete');
        setProfileExists(false);
      } else if (profileCheckResult && !profileCheckResult.has_profile) {
        console.log('⏭️ CandidateDashboard: Backend says profile is incomplete');
        setProfileExists(false);
      } else if (profileCheckResult && profileCheckResult.has_profile && (profileCheckResult.profile_type === 'self_owned' || profileCheckResult.profile_type === 'job_seeker')) {
        console.log('✅ CandidateDashboard: Complete profile found in backend, profile exists');
        setProfileExists(true);
      } else if (profileCheckResult && profileCheckResult.can_claim) {
        console.log('🎯 CandidateDashboard: Company-owned profile that can be claimed, profile incomplete');
        setProfileExists(false);
      } else {
        setProfileExists(false);
      }

      const [candidateData, analyticsData, notificationsData] = await Promise.allSettled([
        apiService.getCandidateProfile(),
        apiService.getMyCandidateAnalytics(),
        apiService.getCandidateNotifications(0, 10, false)
      ]);

      if (candidateData.status === 'fulfilled') {
        // Convert Candidate to AIProfileData format if needed
        const candidateProfile = candidateData.value;
        setCandidateProfile(candidateProfile);
        console.log('✅ CandidateDashboard: Candidate profile loaded:', candidateProfile);
      } else {
        setCandidateProfile(null);
        console.error('❌ CandidateDashboard: Failed to load candidate profile:', candidateData.reason);
        // Don't treat this as an error if user skipped onboarding
        if (!hasSkippedOnboarding2) {
          setError('Could not load your profile data');
        }
      }

      if (analyticsData.status === 'fulfilled') {
        console.log('✅ CandidateDashboard: Analytics loaded');
        setAnalytics(analyticsData.value);
      } else {
        console.error('❌ CandidateDashboard: Failed to load analytics:', analyticsData.reason);
      }

      if (notificationsData.status === 'fulfilled') {
        const notificationsResult = notificationsData.value;
        setNotifications(notificationsResult);
        setUnreadCount(notificationsResult.unread_count || 0);
        console.log('✅ CandidateDashboard: Notifications loaded');
      } else {
        console.error('❌ CandidateDashboard: Failed to load notifications:', notificationsData.reason);
        // Don't treat notification loading failure as critical for skipped users
        if (!hasSkippedOnboarding2) {
          console.log('⚠️ CandidateDashboard: Notifications failed to load, but continuing...');
        }
      }

    } catch (error: any) {
      console.error('❌ CandidateDashboard: Error loading dashboard data:', error);

      if (error.message?.includes('Not authenticated') || error.message?.includes('401')) {
        console.log('❌ CandidateDashboard: Authentication error, redirecting to login');
        navigate('/auth/login', { replace: true });
        return;
      }

      // Don't show error for users who skipped onboarding
      const hasSkippedOnboarding2 = sessionStorage.getItem('skip_onboarding') === 'true';
      if (!hasSkippedOnboarding2) {
        setError('Failed to load dashboard data');
      } else {
        console.log('⏭️ CandidateDashboard: User skipped onboarding, ignoring dashboard data load errors');
        setProfileExists(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResumeUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    setIsProcessing(true);
    setUploadError('');
    setUploadSuccess('');
    setProcessingStatus('Uploading resume...');

    try {
      const response = await apiService.createCandidateProfileFromResume(file);

      if (response.success && response.task_id) {
        setProcessingStatus('Processing resume with AI...');

        // Save processing state for persistence
        const processingState = {
          taskId: response.task_id,
          isProcessing: true,
          status: 'Processing resume with AI...',
          timestamp: Date.now()
        };
        sessionStorage.setItem('dashboard_processing_state', JSON.stringify(processingState));

        pollTaskStatus(response.task_id);
      } else {
        throw new Error(response.message || 'Failed to start resume processing');
      }

    } catch (error: any) {
      setUploadError(error.message || 'Failed to process resume');
      setIsProcessing(false);
      setProcessingStatus('');
      // Clear processing state on error
      sessionStorage.removeItem('dashboard_processing_state');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const pollTaskStatus = async (taskId: string) => {
    try {
      const statusResponse = await apiService.getCandidateProfileCreationStatus(taskId);

      if (statusResponse.status === 'completed') {
        setIsProcessing(false);
        setProcessingStatus('');

        // Clear processing state on completion
        sessionStorage.removeItem('dashboard_processing_state');

        if (statusResponse.result && statusResponse.result.extracted_data) {
          const extractedData = statusResponse.result.extracted_data;

          // Fetch current profile to use as fallback
          try {
            const currentProfile = await apiService.getCandidateProfile();

            // Add comprehensive fallback data from current profile and user
            if (!extractedData.contact_info) {
              extractedData.contact_info = {};
            }

            // Fill in missing contact info with fallbacks
            extractedData.contact_info = {
              first_name: extractedData.contact_info.first_name || currentProfile.first_name || user?.first_name || '',
              last_name: extractedData.contact_info.last_name || currentProfile.last_name || user?.last_name || '',
              email: extractedData.contact_info.email || currentProfile.email || user?.email || '',
              country_code: currentProfile.country_code || '',
              phone: currentProfile.phone || '',
              address: extractedData.contact_info.address || currentProfile.location || '',
              city: extractedData.contact_info.city || '',
              state: extractedData.contact_info.state || '',
              country: extractedData.contact_info.country || '',
              postal_code: extractedData.contact_info.postal_code || '',
              linkedin_url: extractedData.contact_info.linkedin_url || currentProfile.linkedin_url || '',
              github_url: extractedData.contact_info.github_url || currentProfile.github_url || '',
              portfolio_url: extractedData.contact_info.portfolio_url || currentProfile.portfolio_url || '',
              personal_website: extractedData.contact_info.personal_website || currentProfile.personal_website || '',
              confidence: extractedData.contact_info.confidence || 0.0
            };

            // Fill in missing professional summary with fallbacks
            if (!extractedData.professional_summary) {
              extractedData.professional_summary = {};
            }

            extractedData.professional_summary = {
              ...extractedData.professional_summary,
              summary: extractedData.professional_summary.summary || currentProfile.professional_summary || '',
              years_experience: extractedData.professional_summary.years_experience || currentProfile.years_of_experience || 0,
              current_title: extractedData.professional_summary.current_title || currentProfile.current_job_title || '',
              availability: extractedData.professional_summary.availability || currentProfile.availability || '',
              work_type_preference: extractedData.professional_summary.work_type_preference || currentProfile.preferred_work_type || '',
              willing_to_relocate: extractedData.professional_summary.willing_to_relocate || currentProfile.willing_to_relocate || false,
              expected_salary: extractedData.professional_summary.expected_salary || currentProfile.expected_salary || null
            };

            // Fill in work experience if missing
            if (!extractedData.work_experience || extractedData.work_experience.length === 0) {
              if (currentProfile.current_job_title || currentProfile.current_company) {
                extractedData.work_experience = [{
                  job_title: currentProfile.current_job_title || '',
                  company_name: currentProfile.current_company || '',
                  is_current: true,
                  description: '',
                  responsibilities: [],
                  achievements: [],
                  confidence: 0.5
                }];
              }
            }

          } catch (error) {
            console.error('Error fetching current profile for fallback:', error);

            // Basic fallback without current profile
            if (!extractedData.contact_info) {
              extractedData.contact_info = {};
            }

            extractedData.contact_info = {
              ...extractedData.contact_info,
              first_name: extractedData.contact_info.first_name || user?.first_name || '',
              last_name: extractedData.contact_info.last_name || user?.last_name || '',
              email: extractedData.contact_info.email || user?.email || '',
              phone: extractedData.contact_info.phone || '',
            };
          }

          setAiProfileData(extractedData);
          setShowProfileReview(true);

          // Save profile data for review persistence
          sessionStorage.setItem('dashboard_profile_data', JSON.stringify(extractedData));
        } else {
          setHasProfile(true);
          setProfileExists(true);
          setUploadSuccess('Profile created successfully! You can now be discovered by recruiters.');
        }

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

      } else if (statusResponse.status === 'failed') {
        setIsProcessing(false);
        setProcessingStatus('');
        setUploadError(statusResponse.error || 'Resume processing failed');

        // Clear processing state on failure
        sessionStorage.removeItem('dashboard_processing_state');

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

      } else if (statusResponse.status === 'processing') {
        const meta = statusResponse.meta || {};
        let currentStatus = 'Processing resume...';

        if (meta.status === 'processing_resume') {
          currentStatus = 'Analyzing resume with AI...';
        } else if (meta.status === 'creating_profile') {
          currentStatus = 'Creating your profile...';
        } else if (meta.status === 'uploading_file') {
          currentStatus = 'Saving your resume...';
        }

        setProcessingStatus(currentStatus);

        // Update processing state with current status
        const processingState = {
          taskId,
          isProcessing: true,
          status: currentStatus,
          timestamp: Date.now()
        };
        sessionStorage.setItem('dashboard_processing_state', JSON.stringify(processingState));

        setTimeout(() => pollTaskStatus(taskId), 2000);

      } else {
        const currentStatus = 'Resume processing queued...';
        setProcessingStatus(currentStatus);

        // Update processing state
        const processingState = {
          taskId,
          isProcessing: true,
          status: currentStatus,
          timestamp: Date.now()
        };
        sessionStorage.setItem('dashboard_processing_state', JSON.stringify(processingState));

        setTimeout(() => pollTaskStatus(taskId), 2000);
      }

    } catch (error: any) {
      console.error('Error polling task status:', error);

      // Check if it's an auth error and try to reconnect
      if (error.message?.includes('401') || error.message?.includes('Could not validate credentials')) {
        console.log('🔄 Authentication error during processing, retrying...');
        setProcessingStatus('Reconnecting...');
        setTimeout(() => pollTaskStatus(taskId), 3000);
        return;
      }

      setUploadError('Failed to check processing status');
      setIsProcessing(false);
      setProcessingStatus('');

      // Clear processing state on error
      sessionStorage.removeItem('dashboard_processing_state');

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Helper function to safely render any value
  const safeRender = (value: any, fallback: string = ''): string => {
    if (value === null || value === undefined || value === '') return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : fallback;
    }
    if (typeof value === 'object') {
      if (value.summary) return value.summary;
      if (value.objective) return value.objective;
      if (value.name) return value.name;
      if (value.title) return value.title;
      return fallback;
    }
    return String(value);
  };

  // Helper function to get professional summary
  const getProfessionalSummary = (summary: ProfessionalSummary | null | undefined): string => {
    if (!summary) return '';
    if (summary.summary) return summary.summary;
    if (summary.objective) return summary.objective;
    return '';
  };

  // Helper function to get location info
  const getLocationInfo = (): string => {
    if (!aiProfileData?.contact_info) return '';

    const contact = aiProfileData.contact_info;
    if (contact.address) return contact.address;

    const locationParts = [];
    if (contact.city) locationParts.push(contact.city);
    if (contact.state) locationParts.push(contact.state);
    if (locationParts.length > 0) return locationParts.join(', ');

    if (contact.country) return contact.country;
    return '';
  };

  const handleSaveProfile = async () => {
    if (!aiProfileData) return;

    try {
      const countryCode = sanitizeCountryCodeInput(aiProfileData.contact_info?.country_code || '');
      const phone = sanitizePhoneInput(aiProfileData.contact_info?.phone || '');

      if (countryCode && !/^\+?\d{1,4}$/.test(countryCode)) {
        setPhoneValidationError('Please enter a valid country code');
        return;
      }

      const phoneValidation = validatePhone(phone, false);
      if (!phoneValidation.isValid) {
        setPhoneValidationError(phoneValidation.message);
        return;
      }

      setPhoneValidationError('');

      const profileUpdateData = {
        // Extract contact information
        first_name: aiProfileData.contact_info?.first_name || '',
        last_name: aiProfileData.contact_info?.last_name || '',
        country_code: countryCode,
        phone,
        location: aiProfileData.contact_info?.address ||
          (aiProfileData.contact_info?.city && aiProfileData.contact_info?.state
            ? `${aiProfileData.contact_info.city}, ${aiProfileData.contact_info.state}`
            : aiProfileData.contact_info?.city || ''),

        // Extract professional summary as string, not object
        professional_summary: aiProfileData.professional_summary?.summary ||
          aiProfileData.professional_summary?.objective || '',

        // Extract current position from first work experience
        current_job_title: aiProfileData.work_experience?.[0]?.job_title || '',
        current_company: aiProfileData.work_experience?.[0]?.company_name || '',

        // Extract years of experience
        years_of_experience: aiProfileData.professional_summary?.years_experience || 0,

        // Extract work preferences
        expected_salary: aiProfileData.professional_summary?.expected_salary || null,
        availability: aiProfileData.professional_summary?.availability || '',
        preferred_work_type: aiProfileData.professional_summary?.work_type_preference || '',
        willing_to_relocate: aiProfileData.professional_summary?.willing_to_relocate || false,

        // Social profiles from contact info
        linkedin_url: aiProfileData.contact_info?.linkedin_url || '',
        github_url: aiProfileData.contact_info?.github_url || '',
        portfolio_url: aiProfileData.contact_info?.portfolio_url || '',
        personal_website: aiProfileData.contact_info?.personal_website || ''
      };

      const cleanedData = Object.fromEntries(
        Object.entries(profileUpdateData).filter(([_, value]) =>
          value !== null && value !== undefined
        )
      );

      await apiService.updateCandidateProfile(cleanedData);

      // Re-fetch candidate profile to confirm saved data
      const updatedProfile = await apiService.getCandidateProfile();
      setCandidateProfile(updatedProfile);

      // Update React user state so UI re-renders immediately (e.g. welcome header)
      updateUserState({
        first_name: profileUpdateData.first_name || user?.first_name || '',
        last_name: profileUpdateData.last_name || user?.last_name || '',
      });

      // Clear saved profile data since profile is now created
      sessionStorage.removeItem('dashboard_profile_data');
      sessionStorage.setItem('profile_creation_completed', 'true');
      sessionStorage.removeItem('skip_onboarding');

      setHasProfile(true);
      setProfileExists(true);
      setShowProfileReview(false);
      setUploadSuccess('Profile created successfully! You can now be discovered by recruiters.');
    } catch (error: any) {
      setUploadError(error.message || 'Failed to save profile');
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleGeneratePublicLink = async () => {
    try {
      const response = await apiService.generatePublicProfileLink();
      setPublicProfileUrl(response.public_url);
    } catch (error: any) {
      setUploadError(error.message || 'Failed to generate public profile link');
    }
  };

  const handleGenerateResume = async () => {
    setIsGeneratingResume(true);
    try {
      const response = await apiService.generateResumeTemplate();
      setUploadSuccess(`Resume generated successfully! Downloaded as ${response.filename}`);
      setLastResumeUrl(''); // Clear any previous URL since we now download directly
    } catch (error: any) {
      setUploadError(error.message || 'Failed to generate resume template');
    } finally {
      setIsGeneratingResume(false);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await apiService.markNotificationRead(notificationId);
      if (profileExists) {
        const updatedNotifications = await apiService.getCandidateNotifications(0, 5);
        setNotifications(updatedNotifications);
      }
    } catch (error: any) {
      console.error('Error marking notification as read:', error);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'low': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'recruiter_message': return '💬';
      case 'interview_invitation': return '📅';
      case 'profile_view': return '👁️';
      case 'job_match': return '🎯';
      case 'application_update': return '📋';
      case 'skill_verification': return '🏆';
      case 'background_check': return '✅';
      default: return '📢';
    }
  };

  const handleCopy = async () => {
    if (!publicProfileUrl) return;

    // Check if the modern Clipboard API is available
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(publicProfileUrl);
        setToast({ message: 'Link copied successfully!', type: 'success' });
        return; // Exit if successful
      } catch (err) {
        console.error('Modern copy failed', err);
      }
    }

    // Fallback for HTTP/Live IP Address
    try {
      const textArea = document.createElement("textarea");
      textArea.value = publicProfileUrl;

      // Hide the textarea off-screen
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      textArea.style.top = "0";

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        setToast({ message: 'Link copied successfully!', type: 'success' });
      } else {
        throw new Error('Fallback failed');
      }
    } catch (err) {
      setToast({ message: 'Failed to copy link', type: 'error' });
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!profileExists && !showProfileReview) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Welcome to TalentKey.AI, {user?.first_name}!
          </h1>
          <p className="text-lg text-gray-600">
            Let's create your professional profile using AI
          </p>
        </div>

        {uploadSuccess && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {uploadSuccess}
            </div>
          </div>
        )}

        {uploadError && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              {uploadError}
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8">
          <div className="text-center mb-6">
            <div className="mx-auto h-16 w-16 text-emerald-600 mb-4">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-2">Create Your Profile with AI</h3>
            <p className="text-gray-600 mb-6">
              Upload your resume and our AI will automatically extract and organize your professional information
            </p>

            <button
              onClick={handleUploadClick}
              disabled={isProcessing}
              className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white transition-colors ${isProcessing
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500'
                }`}
            >
              {isProcessing ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {processingStatus || 'Processing Resume...'}
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Upload Resume
                </>
              )}
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.txt"
              onChange={(e) => handleResumeUpload(e.target.files)}
              className="hidden"
            />

            <div className="mt-4 text-sm text-gray-500 space-y-1">
              <p>• Supported formats: PDF, DOC, DOCX, TXT</p>
              <p>• Maximum file size: 10MB</p>
              <p>• Your data is secure and encrypted</p>
            </div>
          </div>

          <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
            <h4 className="text-sm font-semibold text-emerald-800 mb-3">
              AI will extract and organize:
            </h4>
            <div className="grid grid-cols-2 gap-2 text-xs text-emerald-700">
              <div>• Personal information</div>
              <div>• Work experience</div>
              <div>• Skills and technologies</div>
              <div>• Education background</div>
              <div>• Professional summary</div>
              <div>• Contact details</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showProfileReview && aiProfileData) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Review Your AI-Generated Profile
          </h1>
          <p className="text-gray-600">
            Please review and edit the information extracted from your resume
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={aiProfileData.contact_info?.first_name || ''}
                  onChange={(e) => setAiProfileData({
                    ...aiProfileData,
                    contact_info: { ...aiProfileData.contact_info, first_name: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={aiProfileData.contact_info?.last_name || ''}
                  onChange={(e) => setAiProfileData({
                    ...aiProfileData,
                    contact_info: { ...aiProfileData.contact_info, last_name: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={aiProfileData.contact_info?.email || ''}
                  onChange={(e) => setAiProfileData({
                    ...aiProfileData,
                    contact_info: { ...aiProfileData.contact_info, email: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country Code</label>
                <input
                  type="tel"
                  value={aiProfileData.contact_info?.country_code || ''}
                  onChange={(e) => {
                    const countryCode = sanitizeCountryCodeInput(e.target.value);
                    setPhoneValidationError('');
                    setAiProfileData({
                      ...aiProfileData,
                      contact_info: { ...aiProfileData.contact_info, country_code: countryCode }
                    });
                  }}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                    phoneValidationError.toLowerCase().includes('country code')
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-gray-300'
                  }`}
                  placeholder="+1"
                  inputMode="tel"
                  pattern="[0-9+]*"
                />
                {phoneValidationError.toLowerCase().includes('country code') && (
                  <p className="mt-1 text-sm text-red-600">{phoneValidationError}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={aiProfileData.contact_info?.phone || ''}
                  inputMode="numeric"
                  onKeyDown={(e) => {
                   // Allow digits
                   if (/^[0-9]$/.test(e.key)) return;
                   const allowedKeys = [
                    "Backspace",
                    "Delete",
                    "Tab",
                    "ArrowLeft",
                    "ArrowRight",
                    "Home",
                    "End",
                  ];

                 if (allowedKeys.includes(e.key)) return;

                     e.preventDefault();
                  }}
                  onChange={(e) => {
                    const phone = sanitizePhoneInput(e.target.value);
                    const phoneValidation = validatePhone(phone, false);
                    setPhoneValidationError(phoneValidation.isValid ? '' : phoneValidation.message);
                    setAiProfileData({
                      ...aiProfileData,
                      contact_info: { ...aiProfileData.contact_info, phone }
                    });
                  }}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                    phoneValidationError && !phoneValidationError.toLowerCase().includes('country code')
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-gray-300'
                  }`}
                  placeholder="5551234567"
                  pattern="[0-9]*"
                />
                {phoneValidationError && !phoneValidationError.toLowerCase().includes('country code') && (
                  <p className="mt-1 text-sm text-red-600">{phoneValidationError}</p>
                )}
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  value={getLocationInfo()}
                  onChange={(e) => setAiProfileData({
                    ...aiProfileData,
                    contact_info: { ...aiProfileData.contact_info, address: e.target.value }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Professional Summary</h3>
            <textarea
              value={getProfessionalSummary(aiProfileData.professional_summary)}
              onChange={(e) => setAiProfileData({
                ...aiProfileData,
                professional_summary: { ...aiProfileData.professional_summary, summary: e.target.value }
              })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Position</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                <input
                  type="text"
                  value={aiProfileData.work_experience?.[0]?.job_title || ''}
                  onChange={(e) => {
                    const updatedExperience = [...(aiProfileData.work_experience || [])];
                    if (updatedExperience.length === 0) {
                      updatedExperience.push({ job_title: e.target.value });
                    } else {
                      updatedExperience[0] = { ...updatedExperience[0], job_title: e.target.value };
                    }
                    setAiProfileData({
                      ...aiProfileData,
                      work_experience: updatedExperience
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                <input
                  type="text"
                  value={aiProfileData.work_experience?.[0]?.company_name || ''}
                  onChange={(e) => {
                    const updatedExperience = [...(aiProfileData.work_experience || [])];
                    if (updatedExperience.length === 0) {
                      updatedExperience.push({ company_name: e.target.value });
                    } else {
                      updatedExperience[0] = { ...updatedExperience[0], company_name: e.target.value };
                    }
                    setAiProfileData({
                      ...aiProfileData,
                      work_experience: updatedExperience
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Years of Experience</label>
                <input
                  type="number"
                  value={aiProfileData.professional_summary?.years_experience || 0}
                  onChange={(e) => setAiProfileData({
                    ...aiProfileData,
                    professional_summary: { ...aiProfileData.professional_summary, years_experience: parseInt(e.target.value) || 0 }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Skills</h3>
            <div className="flex flex-wrap gap-2">
              {(aiProfileData.skills || []).map((skill, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-800"
                >
                  {skill.name}
                  <button
                    onClick={() => {
                      const newSkills = (aiProfileData.skills || []).filter((_, i) => i !== index);
                      setAiProfileData({ ...aiProfileData, skills: newSkills });
                    }}
                    className="ml-2 text-emerald-600 hover:text-emerald-800"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="flex space-x-4 pt-6 border-t">
            <button
              onClick={handleSaveProfile}
              className="flex-1 bg-emerald-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-emerald-700 transition-colors"
            >
              Save Profile
            </button>
            <button
              onClick={() => setShowProfileReview(false)}
              className="flex-1 bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              Upload Different Resume
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome back, {user?.first_name}!
          </h1>
          <p className="text-gray-600 mt-2">
            Here's what's happening with your job search
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Profile Status</h2>
              {profileExists ? (
                <div className="flex items-center text-emerald-600">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">Profile Complete</span>
                </div>
              ) : (
                <div className="flex items-center text-amber-600">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">Profile Incomplete</span>
                </div>
              )}
            </div>
            <div>
              {!profileExists && (
                <a
                  href="/candidate/profile"
                  className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  Complete Profile
                </a>
              )}
            </div>
          </div>
        </div>

        {profileExists && analytics && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Profile Views</p>
                  <p className="text-2xl font-bold text-gray-900">{analytics.profile_views}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Search Appearances</p>
                  <p className="text-2xl font-bold text-gray-900">{analytics.search_appearances}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Recruiter Contacts</p>
                  <p className="text-2xl font-bold text-gray-900">{analytics.recruiter_contacts}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Profile Strength</p>
                  <div className="flex items-center">
                    <div className="flex items-center">
                      <p className="text-2xl font-bold text-gray-900">{analytics.profile_strength}%</p>
                      <Tooltip
                        content="Basic Info & Education: 35%
Skills & Experience: 35%
Professional Summary: 15%
Extras (Projects, Certifications & Languages): +15%"
                        position="left"
                      >
                        <svg className="w-4 h-4 text-gray-400 ml-1 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                        </svg>
                      </Tooltip>
                    </div>
                    <div className="ml-2 flex-1">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${analytics.profile_strength}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {profileExists && analytics && analytics.recent_activity.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Activity</h2>
            <div className="space-y-3">
              {analytics.recent_activity.slice(0, 5).map((activity, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full mr-3 ${activity.type === 'Profile View' ? 'bg-blue-400' : 'bg-purple-400'
                      }`}></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{activity.type}</p>
                      <p className="text-xs text-gray-600">{activity.company}</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500">{activity.time_ago}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {profileExists && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 rounded-lg p-2 mr-3">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Share Your Profile</h3>
                  <p className="text-sm text-gray-600">Generate a public link to share with anyone</p>
                </div>
              </div>

              {publicProfileUrl ? (
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={publicProfileUrl || ''}
                      readOnly
                      className="flex-1 text-sm bg-gray-50 border border-gray-300 rounded px-3 py-2"
                    />
                    <button
                      onClick={handleCopy}
                      className="text-sm bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 transition-colors"
                    >
                      Copy
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">Share this link to showcase your profile publicly</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">Create a shareable link for your profile that you can use on social media, email signatures, or anywhere else.</p>
                  <button
                    onClick={handleGeneratePublicLink}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Generate Public Link
                  </button>
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="bg-green-100 rounded-lg p-2 mr-3">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3v-6m0 0l-3 3m3-3v6m0 0l3-3m-3 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Download Resume</h3>
                  <p className="text-sm text-gray-600">Generate a professional resume template</p>
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-sm text-gray-600">Create a clean, professional resume using your profile data with our modern templates.</p>
                <button
                  onClick={handleGenerateResume}
                  disabled={isGeneratingResume}
                  className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isGeneratingResume ? 'Generating Resume...' : 'Generate & Download Resume'}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <div className="bg-gray-100 rounded-lg p-2 mr-3">
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Contact Support</h3>
                  <p className="text-sm text-gray-600">Need help? Get in touch with our team</p>
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-sm text-gray-600">Have questions about your profile, features, or need technical assistance? Our support team is here to help.</p>
                <button
                  onClick={() => setShowSupportForm(true)}
                  className="w-full bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Get Support
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Recent Notifications</h2>
              {notifications && notifications.unread_count > 0 && (
                <p className="text-sm text-gray-600 mt-1">
                  You have {notifications.unread_count} unread notification{notifications.unread_count !== 1 ? 's' : ''}
                </p>
              )}
            </div>
            <div className="flex space-x-2">
              {notifications && notifications.total > 5 && (
                <a
                  href="/candidate/notifications"
                  className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  View All
                </a>
              )}
            </div>
          </div>

          {notifications && notifications.notifications.length > 0 ? (
            <div className="space-y-4">
              {notifications.notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`border rounded-lg p-4 transition-all hover:shadow-sm ${notification.is_read ? 'bg-gray-50' : 'bg-white border-emerald-200'
                    }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-lg">{getTypeIcon(notification.type)}</span>
                        <h3 className={`font-medium ${notification.is_read ? 'text-gray-700' : 'text-gray-900'}`}>
                          {notification.title}
                        </h3>
                        <span className={`text-xs px-2 py-1 rounded-full border ${getPriorityColor(notification.priority)}`}>
                          {notification.priority}
                        </span>
                      </div>
                      <p className={`text-sm ${notification.is_read ? 'text-gray-600' : 'text-gray-800'} mb-2`}>
                        {notification.message}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                          {formatTimeAgo(notification.created_at)}
                        </span>
                        <div className="flex space-x-2">
                          {notification.action_text && notification.action_url && (
                            <a
                              href={notification.action_url}
                              className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                            >
                              {notification.action_text}
                            </a>
                          )}
                          {!notification.is_read && (
                            <button
                              onClick={() => handleMarkAsRead(notification.id)}
                              className="text-xs text-gray-500 hover:text-gray-700"
                            >
                              Mark as read
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 text-4xl mb-4">🔔</div>
              <p className="text-gray-600 mb-4">No notifications yet</p>
              <p className="text-sm text-gray-500">
                You'll receive notifications when recruiters view your profile, send messages, or when there are job matches.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Support Ticket Form Modal */}
      <SupportTicketForm
        isOpen={showSupportForm}
        onClose={() => setShowSupportForm(false)}
        source="JOB_SEEKER_PORTAL"
        userInfo={{
          name: [
            candidateProfile?.first_name || user?.first_name,
            candidateProfile?.last_name || user?.last_name
          ]
            .filter(Boolean)
            .join(' ')
            .trim(),
          email: user?.email,
          phone: candidateProfile?.phone || user?.phone
        }}
      />
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)} isVisible={true} />
      )}
    </div>
  );
} 
